import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { AlertTriangle, UploadCloud } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { db } from "../firebase";
import { serverTimestamp, setDoc, getDoc, doc } from "firebase/firestore";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Card } from "./ui/card";

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadComplete: (record: any) => void;
  onSuccess?: () => void;
  affiliations: string[];
}

const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  onUploadComplete,
  affiliations,
  onSuccess,
}) => {
  const [affiliation, setAffiliation] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [month, setMonth] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return [now - 1, now, now + 1, now + 2].map(String);
  }, []);

  const resetAll = () => {
    setAffiliation("");
    setYear("");
    setMonth("");
    setTitle("");
    setFile(null);
    setUploading(false);
    setConfirmOpen(false);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const onDrop = (accepted: File[]) => {
    const f = accepted[0];
    if (f) {
      setFile(f);
      toast.success(`선택됨: ${f.name}`);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
  });

  const validate = () => {
    if (!affiliation) {
      toast.error("고객사를 선택해 주세요.");
      return false;
    }
    if (!year) {
      toast.error("연도를 선택해 주세요.");
      return false;
    }
    if (!month) {
      toast.error("월을 선택해 주세요.");
      return false;
    }
    if (!title || title.length > 60) {
      toast.error("타이틀을 입력해 주세요. (최대 60자)");
      return false;
    }
    if (!file) {
      toast.error("엑셀 파일을 선택해 주세요.");
      return false;
    }
    return true;
  };

  /** posName 변환 맵 */
  const posNameMap: Record<string, Record<string, string>> = {
    KB자산운용: { POS10: "40층", "POS 11": "18층", "POS 12": "41층" },
    우리자산운용: { POS10: "37층", "POS 11": "44층" },
    코인원: { POS10: "45층" },
  };
  const getCustomPosName = (aff: string, posName: string): string =>
    posNameMap[aff]?.[posName] || posName;

  const handleUpload = async () => {
    if (!validate()) return;

    try {
      setUploading(true);

      const reader = new FileReader();
      const fileReadPromise = new Promise<any[]>((resolve, reject) => {
        reader.onload = (e) => {
          try {
            const result = e.target?.result;
            const workbook = XLSX.read(result as string, { type: "binary" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

            const rows = (jsonData as any[])
              .map((row) => ({
                saleDate: row["매출일시"],
                productName: row["메뉴 이름"],
                unitPrice: parseFloat(row["단가"]),
                quantity: parseFloat(row["수량"]),
                totalPrice: parseFloat(row["메뉴별 판매가"]),
                customerName: row["고객명"],
                receiptNumber: row["영수증번호"],
                posAt: getCustomPosName(affiliation, row["포스"]),
              }))
              .filter((r) => !isNaN(r.unitPrice) && !isNaN(r.quantity));

            if (affiliation === "우리자산운용") {
              const receiptCustomerMap: Record<string, string> = {};
              rows.forEach((r) => {
                if (r.customerName && r.receiptNumber) {
                  receiptCustomerMap[r.receiptNumber] = r.customerName;
                }
              });
              rows.forEach((r) => {
                if (
                  (!r.customerName || r.customerName === "") &&
                  r.receiptNumber
                ) {
                  const fixed = receiptCustomerMap[r.receiptNumber];
                  if (fixed) r.customerName = fixed;
                }
              });
            }

            const parseYearMonth = (dateStr: string) => {
              if (!dateStr) return { y: null, m: null };
              const parts = String(dateStr).split(" ")[0].split("-");
              return { y: parts[0], m: parts[1]?.padStart(2, "0") };
            };

            const invalidRows = rows.filter((r) => {
              const { y, m } = parseYearMonth(r.saleDate);
              return y !== year || m !== month;
            });

            if (rows.length === 0)
              return reject(new Error("업로드할 데이터가 없습니다."));
            if (invalidRows.length > 0)
              return reject(
                new Error(
                  `선택한 연(${year})·월(${month})과 다른 데이터가 ${invalidRows.length}건 있습니다.`
                )
              );

            resolve(rows);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
      });

      reader.readAsBinaryString(file!);
      const newRows = await fileReadPromise;

      const docRef = doc(
        db,
        `affiliations/${affiliation}/salesUploads/${year}-${month}`
      );
      const snap = await getDoc(docRef);

      let mergedRows = newRows;
      if (snap.exists()) {
        const oldRows = (snap.data() as any).rows || [];
        const key = (r: any) => `${r.receiptNumber}__${r.productName}`;
        const newKeySet = new Set(newRows.map(key));
        const uniqueOld = oldRows.filter((r: any) => !newKeySet.has(key(r)));
        mergedRows = [...newRows, ...uniqueOld];
      }

      await setDoc(docRef, {
        title,
        affiliation,
        year: parseInt(year),
        month: parseInt(month),
        uploadedAt: serverTimestamp(),
        rows: mergedRows,
        recordCount: mergedRows.length,
      });

      toast.success("업로드가 완료되었습니다.");
      onUploadComplete({
        id: docRef.id,
        title,
        affiliation,
        year: parseInt(year),
        month: parseInt(month),
        rows: mergedRows,
      });
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      toast.error(err?.message || "업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
      setConfirmOpen(false);
    }
  };

  const confirmUpload = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>엑셀 업로드</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>고객사</Label>
                <Select value={affiliation} onValueChange={setAffiliation}>
                  <SelectTrigger>
                    <SelectValue placeholder="고객사 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {affiliations.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>연도</Label>
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger>
                    <SelectValue placeholder="연도 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={y}>
                        {y}년
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>월</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger>
                    <SelectValue placeholder="월 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => {
                      const m = String(i + 1).padStart(2, "0");
                      return (
                        <SelectItem key={m} value={m}>
                          {m}월
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>타이틀</Label>
              <Input
                placeholder="예) 2025-07 KB자산 운용 7월 매출"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>엑셀 파일</Label>
              <div
                {...getRootProps()}
                className={`flex h-36 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed p-4 text-center transition-colors ${
                  isDragActive
                    ? "border-black bg-gray-50"
                    : "border-border"
                }`}
              >
                <input {...getInputProps()} />
                <UploadCloud className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium">
                  여기로 파일을 드래그하거나 클릭하여 선택하세요
                </p>
                <p className="text-xs text-muted-foreground">
                  지원 형식: .xlsx, .xls
                </p>
              </div>
              {file && (
                <p className="text-sm text-muted-foreground">선택됨: {file.name}</p>
              )}
            </div>

            <Card className="p-4">
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                <span>선택된 고객사:</span>
                <span className="font-semibold text-foreground">
                  {affiliation || "-"}
                </span>
                <span>연·월:</span>
                <span className="font-semibold text-foreground">
                  {year && month ? `${year}-${month}` : "-"}
                </span>
                <span>파일:</span>
                <span className="font-semibold text-foreground">
                  {file?.name || "-"}
                </span>
              </div>
            </Card>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={handleClose}>
              취소
            </Button>
            <Button onClick={confirmUpload} disabled={uploading}>
              업로드
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              업로드를 진행할까요?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>다음 정보로 업로드를 진행합니다.</p>
            <Card className="p-4">
              <div className="space-y-1 text-sm">
                <div>
                  <b>고객사</b> : {affiliation}
                </div>
                <div>
                  <b>연·월</b> : {year}-{month}
                </div>
                <div>
                  <b>타이틀</b> : {title}
                </div>
                <div>
                  <b>파일</b> : {file?.name}
                </div>
              </div>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              취소
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? "업로드 중..." : "업로드"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UploadModal;
